import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {LocalStorageService} from 'src/app/local-storage.service';
import {ServiceAppModels} from 'src/app/models/serviceapp-models';
import {MatDialog} from '@angular/material/dialog';
import {ZeitMutationComponent} from './zeiten/zeit-mutation/zeit-mutation.component';
import {BenutzerModels} from 'src/app/models/benutzer-models';
import {Router} from '@angular/router';
import {ServiceAppService} from 'src/app/services/service-app.service';
import {UserAuthenticationService} from 'src/app/services/user-authentication.service';
import {BehaviorSubject, combineLatest, from, Observable, share, switchMap, tap} from 'rxjs';
import {filter, first, map} from 'rxjs/operators';
import {HelperService} from '../../services/helper.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MessageBoxService} from '../../services/message-box.service';
import SA_Objekt = ServiceAppModels.SA_Objekt;
import SA_TimeEntry = ServiceAppModels.SA_TimeEntry;
import Anwender = BenutzerModels.Anwender;

@Component({
    selector: 'app-service-app',
    templateUrl: './service-app.component.html',
    styleUrls: ['./service-app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceAppComponent implements OnInit {
    protected readonly selectedAppointment$: BehaviorSubject<ServiceAppModels.SA_Objekt | null> = new BehaviorSubject<ServiceAppModels.SA_Objekt | null>(null);
    protected readonly currentUser$: Observable<Anwender>;
    protected readonly newTimeEntry$: BehaviorSubject<ServiceAppModels.SA_TimeEntry | null> = new BehaviorSubject<ServiceAppModels.SA_TimeEntry | null>(null);

    protected readonly currTerminExists$ = this.selectedAppointment$.pipe(
        map((x) => !!x),
        share({connector: () => new BehaviorSubject(false)})
    );
    protected readonly currTerminFinished$ = this.selectedAppointment$.pipe(
        map((x) => (x ? x.serviceComplete === 1 : true)),
        share({connector: () => new BehaviorSubject(true)})
    );
    /* Daten */
    private readonly DB_STORAGE_NAME_SA: string = 'ServiceAppDB';

    constructor(private localStorageService: LocalStorageService, private router: Router, public dialog: MatDialog, private svcSA: ServiceAppService, private userAuthService: UserAuthenticationService, private helperService: HelperService, private messageBoxService: MessageBoxService) {
        // Wurde der Browser aktualisiert?
        this.helperService.setPageRefreshURL('/serviceapp/saagenda').then(() => {
            if (this.userAuthService.getCurrentAuthenticatedUser() == undefined) {
                this.router.navigate(['']);
            }
        });

        this.currentUser$ = from(this.svcSA.getAnwenderAsync()).pipe(
            tap({error: (err) => console.error('service-app.component.ts => Fehler in getAnwenderAsnyc:', err)})
        );

        // Alle Checklisten aus der Indexed-DB laden
        this.svcSA.getChecklisten();

        // Alle Rapporte aus der Indexed-DB laden
        this.svcSA.getRapporte();

        // Alle Bilder aus der Indexed-DB laden
        this.svcSA.getBilder();

        // Dem Service-App Service mitteilen, dass diese Methode aufgerufen wird, wenn ein Einsatz gelöscht wurde
        this.svcSA.registerSAHeaderObjectFunc(this.removeAppointment.bind(this));

        this.newTimeEntry$.pipe(filter(x => !!x), switchMap(x => this.saveTimeEntryToIndexedDB(x)), takeUntilDestroyed()).subscribe();
    }

    ngOnInit(): void {
        this.svcSA.loadDataForServiceApp();
    }

    public reloadAppointmentByServiceID(serviceID: string) {
        let termin: ServiceAppModels.SA_Objekt | undefined = this.svcSA.getTerminByServiceID(serviceID);
        if (termin) {
            this.reloadAppointment(termin);
        }
    }

    public reloadAppointment(termin?: ServiceAppModels.SA_Objekt) {
        this.selectedAppointment$.next(termin ? termin : null);
    }

    protected removeAppointment() {
        this.svcSA.removeTermin();
        this.selectedAppointment$.next(null);

        /* Eventuell muss noch die Seite gewechselt werden */
        let currentPage: string = this.router.url;
        if (currentPage === '/serviceapp/sapics' || currentPage === '/serviceapp/sadocs' || currentPage === '/serviceapp/sarapport' || currentPage === '/serviceapp/sapositionen') {
            // Wenn die Seite ändert, werden die Daten automatisch neu geladen
            this.router.navigate(['serviceapp/saagenda']);
        }
    }

    protected openKalender() {
        this.router.navigate(['serviceapp/saagenda']);
    }

    protected openMaterial() {
        this.router.navigate(['serviceapp/sapositionen']);
    }

    protected openFotos() {
        this.router.navigate(['serviceapp/sapics']);
    }

    protected openRapport() {
        this.router.navigate(['serviceapp/sarapport']);
    }

    protected openZeiten() {
        this.router.navigate(['serviceapp/sazeiten']);
    }

    protected openDokumente() {
        this.router.navigate(['serviceapp/sadocs']);
    }

    protected openAuftraege() {
        this.router.navigate(['serviceapp/saauftraege']);
    }

    protected addWorkingHours() {
        combineLatest({user: this.currentUser$, appointment: this.selectedAppointment$}).pipe(first(),
            filter(({appointment}) => this.canOpenAddWorkingTimeDialog(appointment)),
            switchMap(({appointment, user}) => {
                return this.openTimeMutationDialog(appointment, user);
            }),
            tap(result => {
                this.newTimeEntry$.next(result);
            })).subscribe();
    }

    protected showAppointmentDetails() {
        this.currTerminFinished$.pipe(first(), tap((finished) => {
            this.svcSA.showAppointmentDetails(finished);
        })).subscribe();
    }

    protected createAppointment() {
        let calenderAddBtn: HTMLButtonElement | null = document.querySelector('.newEinsatzFakeBtn');
        if (calenderAddBtn) {
            calenderAddBtn.click();
        }
    }

    protected getOrderCustomerLine(objectID: string) {
        return this.svcSA.getAuftragsKundenAdresseZeile(objectID);
    }

    private openTimeMutationDialog(appointment: SA_Objekt | null, user: Anwender | null): Observable<any> {
        return this.dialog.open(
            ZeitMutationComponent,
            {
                width: window.innerWidth > window.innerHeight ? '100%' : '',
                disableClose: true,
                data: {sA_Termin: appointment, sA_Anwender: user},
                panelClass: 'swissdefault-dialog-container',
            }).afterClosed();
    }

    private async saveTimeEntryToIndexedDB(timeEntry: SA_TimeEntry) {
        await this.svcSA.saveTimeEntryToIndexedDB(timeEntry);

        // Zeiten müssen in der Komponente "Zeiten" neu geladen werden.
        if (this.router.url != '/serviceapp/sazeiten') { // Wenn die Seite ändert, werden die Daten automatisch neu geladen
            await this.router.navigate(['serviceapp/sazeiten']);
        }
    }

    private canOpenAddWorkingTimeDialog(appointment: SA_Objekt | null) {
        if ((appointment && !appointment.serviceComplete) || !appointment) {
            return true;
        }
        this.messageBoxService.showErrorByLanguageKey('MAIN_APP_SERVICE_APP.ERROR_DIALOG.CANT_ADD_TIME.text', 'MAIN_APP_SERVICE_APP.ERROR_DIALOG.CANT_ADD_TIME.title');
        return false;
    }

}
